import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, Image } from '@react-pdf/renderer';

const TEAL = '#1B6B6B';
const TEAL_PALE = '#E0F4F4';
const TEAL_MID = '#2E8B8B';
const GOLD = '#E8C87A';
const GOLD_LIGHT = '#FDF6E3';
const WHITE = '#FFFFFF';

const DARK = '#0D2B2B';
const MID = '#2A4A4A';
const RED_SOFT = '#C62828';
const RED_LIGHT = '#FFEBEE';

function clean(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .replace(/[\u{2700}-\u{27BF}]/gu, '')
    .replace(/■/g, '-')
    .trim();
}

function cleanReport(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/#{1,6}\s/g, '');
}

function parseReport(rawText) {
  const result = { citation: '', intro: '', dimensions: [], top3: [], explore: [], avoid: [], conseils: [], synthese: [] };
  if (!rawText) return result;

  const text = cleanReport(rawText);
  const sections = text.split('---').map(s => s.trim()).filter(Boolean);

  sections.forEach((section) => {
    const lines = section.split('\n').map(l => l.trim()).filter(Boolean);

    const isSection1 = section.includes('CITATION') || (lines.some(l => l.includes('"') || l.includes('\u201c')));
    const isSection2 = section.includes('INTRODUCTION') || section.includes('SECTION 2');
    const isSection3 = section.includes('DIMENSIONS') || section.includes('SECTION 3') || lines.some(l => l.trim().startsWith('CORPS'));
    const isSection4 = section.includes('TOP 3') || section.includes('SECTION 4') || lines.some(l => l.trim().startsWith('#1'));
    const isSection5 = (section.includes('EXPLORER') || section.includes('SECTION 5')) && !section.includes('CORRESPOND');
    const isSection6 = section.includes('CORRESPOND PAS') || section.includes('SECTION 6') || section.includes('ÉVITER');
    const isSection7 = section.includes('CONSEILS') || section.includes('SECTION 7') || section.includes('PARENTS');
    const isSection8 = section.includes('SYNTHESE') || section.includes('TABLEAU') || section.includes('SECTION 8') || section.includes('RÉSUMÉ');

    if (isSection1 && !result.citation) {
      const citLine = lines.find(l => l.includes('"') || l.includes('\u201c') || l.includes('\u00ab'));
      if (citLine) {
        result.citation = '"' + citLine.replace(/^.*?["\u00ab\u201c]/, '').replace(/["\u00bb\u201d].*$/, '') + '"';
      }
    }
    else if (isSection2 && !result.intro) {
      result.intro = lines.filter(l => !l.includes('SECTION') && !l.includes('INTRODUCTION')).join(' ');
    }
    else if (isSection3 && result.dimensions.length === 0) {
      ['CORPS', 'SOCIAL', 'MENTAL', 'MOTEUR'].forEach(dim => {
        const line = lines.find(l => l.trim().startsWith(dim));
        if (line) {
          const parts = line.split('|').map(p => p.trim());
          result.dimensions.push({ label: dim, title: parts[1] || '', desc: parts.slice(2).join(' ') || '' });
        }
      });
    }
    else if (isSection4 && result.top3.length === 0) {
      ['#1', '#2', '#3'].forEach(rank => {
        const line = lines.find(l => l.trim().startsWith(rank));
        if (line) {
          const parts = line.split('|').map(p => p.trim());
          result.top3.push({ rank, name: parts[1] || '', why: parts[2] || '', tags: parts[3] || '' });
        }
      });
    }
    else if (isSection5 && result.explore.length === 0) {
      lines.filter(l => l.includes('|') && !l.includes('SECTION') && !l.includes('EXPLORER')).forEach(line => {
        const parts = line.split('|').map(p => p.trim());
        if (parts[0]) result.explore.push({ name: parts[0], why: parts[1] || '' });
      });
    }
    else if (isSection6 && result.avoid.length === 0) {
      lines.filter(l => l.includes('|') && !l.includes('SECTION') && !l.includes('CORRESPOND') && !l.includes('ÉVITER')).forEach(line => {
        const parts = line.split('|').map(p => p.trim());
        if (parts[0]) result.avoid.push({ name: parts[0], why: parts[1] || '' });
      });
    }
    else if (isSection7 && result.conseils.length === 0) {
      lines.filter(l => !l.includes('SECTION') && !l.includes('CONSEILS') && !l.includes('PARENTS') && l.includes('|')).forEach(line => {
        const parts = line.split('|').map(p => p.trim());
        const numMatch = parts[0].match(/^(\d+)\.?\s*(.*)/);
        result.conseils.push({
          num: numMatch ? numMatch[1] : '',
          title: numMatch ? numMatch[2] : parts[0],
          text: parts[1] || ''
        });
      });
    }
    else if (isSection8 && result.synthese.length === 0) {
      lines.filter(l => l.includes('|') && !l.includes('SECTION') && !l.includes('SYNTHESE') && !l.includes('Activite')).forEach(line => {
        const parts = line.split('|').map(p => p.trim());
        if (parts[0]) result.synthese.push({ name: parts[0], adequation: parts[1] || '', why: parts[2] || '' });
      });
    }
  });

  return result;
}

const s = StyleSheet.create({
  page: { padding: 36, fontFamily: 'Helvetica', backgroundColor: WHITE, fontSize: 10 },
  logo: { width: 120, height: 30, marginBottom: 8 },

  // COVER
  coverBg: { backgroundColor: TEAL, padding: 32, marginBottom: 16 },
  coverLabel: { fontSize: 8, color: 'rgba(255,255,255,0.55)', letterSpacing: 2, marginBottom: 6 },
  coverTitle: { fontSize: 34, fontWeight: 'bold', color: WHITE, marginBottom: 3 },
  coverSub: { fontSize: 11, color: '#A0D4D4', marginBottom: 16 },
  coverBadge1: { backgroundColor: 'rgba(255,255,255,0.15)', padding: '8 12', borderRadius: 6, marginBottom: 6 },
  coverBadge2: { backgroundColor: 'rgba(255,255,255,0.08)', padding: '6 12', borderRadius: 6, marginBottom: 12 },
  coverProfileName: { fontSize: 16, fontWeight: 'bold', color: WHITE },
  coverProfileLabel: { fontSize: 9, color: '#A0D4D4', marginTop: 2 },
  coverCitation: { fontSize: 10, color: GOLD, fontStyle: 'italic', marginTop: 8 },
  coverMeta: { fontSize: 9, color: 'rgba(255,255,255,0.45)', marginTop: 8 },

  // SECTION HEADER
  secH: { backgroundColor: TEAL, padding: '6 10', marginBottom: 8, marginTop: 12 },
  secHRed: { backgroundColor: RED_SOFT, padding: '6 10', marginBottom: 8, marginTop: 12 },
  secHGold: { backgroundColor: GOLD, padding: '6 10', marginBottom: 8, marginTop: 12 },
  secHText: { fontSize: 9, fontWeight: 'bold', color: WHITE, letterSpacing: 1 },
  secHTextDark: { fontSize: 9, fontWeight: 'bold', color: DARK, letterSpacing: 1 },

  // INTRO
  introText: { fontSize: 10, color: MID, lineHeight: 1.6, marginBottom: 6 },

  // DIMENSIONS
  dimRow: { flexDirection: 'row', marginBottom: 6, paddingBottom: 6, borderBottomWidth: 1, borderBottomColor: TEAL_PALE },
  dimLabel: { width: 55, fontSize: 9, fontWeight: 'bold', color: TEAL },
  dimTitle: { width: 120, fontSize: 9, fontWeight: 'bold', color: DARK },
  dimDesc: { flex: 1, fontSize: 9, color: MID, lineHeight: 1.5 },

  // TOP 3
  actCard: { backgroundColor: TEAL_PALE, padding: '10 12', marginBottom: 8, borderLeftWidth: 4, borderLeftColor: TEAL },
  actRank: { fontSize: 14, fontWeight: 'bold', color: TEAL, marginBottom: 2 },
  actName: { fontSize: 12, fontWeight: 'bold', color: DARK, marginBottom: 4 },
  actWhy: { fontSize: 10, color: MID, lineHeight: 1.5, marginBottom: 4 },
  actTags: { fontSize: 8, color: TEAL_MID },

  // EXPLORER
  exploreRow: { flexDirection: 'row', marginBottom: 5, paddingBottom: 5, borderBottomWidth: 1, borderBottomColor: TEAL_PALE },
  exploreName: { width: 150, fontSize: 9, fontWeight: 'bold', color: TEAL },
  exploreWhy: { flex: 1, fontSize: 9, color: MID, lineHeight: 1.4 },

  // AVOID
  avoidCard: { backgroundColor: RED_LIGHT, borderLeftWidth: 3, borderLeftColor: RED_SOFT, padding: '8 10', marginBottom: 6 },
  avoidName: { fontSize: 9, fontWeight: 'bold', color: RED_SOFT, marginBottom: 2 },
  avoidWhy: { fontSize: 9, color: '#7B1A1A', lineHeight: 1.4 },

  // CONSEILS
  conseilCard: { backgroundColor: TEAL_PALE, padding: '10 12', marginBottom: 6 },
  conseilRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 4 },
  conseilNum: { fontSize: 13, fontWeight: 'bold', color: TEAL, marginRight: 8, width: 18 },
  conseilTitle: { fontSize: 10, fontWeight: 'bold', color: DARK, flex: 1 },
  conseilText: { fontSize: 9, color: MID, lineHeight: 1.5, marginTop: 3 },

  // SYNTHESE TABLE
  tableHeader: { backgroundColor: TEAL, padding: '5 8', flexDirection: 'row' },
  tableHeaderText: { fontSize: 9, fontWeight: 'bold', color: WHITE },
  tableRow: { flexDirection: 'row', paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: TEAL_PALE },
  tableCell1: { width: 150, fontSize: 9, color: DARK, paddingHorizontal: 4 },
  tableCell2: { width: 60, fontSize: 9, color: TEAL, paddingHorizontal: 4 },
  tableCell3: { flex: 1, fontSize: 9, color: MID, paddingHorizontal: 4 },

  // NOTE
  noteBox: { backgroundColor: GOLD_LIGHT, borderLeftWidth: 4, borderLeftColor: GOLD, padding: '12 14', marginTop: 16 },
  noteText: { fontSize: 9, color: '#6B5000', lineHeight: 1.6, fontStyle: 'italic' },

  // PAGE
  footer: { position: 'absolute', bottom: 20, left: 36, right: 36, flexDirection: 'row', justifyContent: 'space-between' },
  footerText: { fontSize: 7, color: '#9BC4C4' },
  pageHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 12, paddingBottom: 6, borderBottomWidth: 1, borderBottomColor: TEAL_PALE },
  pageHeaderText: { fontSize: 8, color: TEAL_MID },
});

export function MatchkidReport({ childName, childAge, gender, primaryProfile, secondaryProfile, reportText }) {
  const data = parseReport(reportText);
  const today = new Date().toLocaleDateString('fr-FR');
  const pName = clean(primaryProfile?.name || '');
  const sName = clean(secondaryProfile?.name || '');
  const cName = clean(childName || 'Votre enfant');
  // headerLeft removed
  const headerRight = `${pName} - ${sName}`;
  const noteText = gender === "Fille" ? `Ce rapport est un guide, pas une prescription. ${cName} reste la meilleure experte d'elle-même. La meilleure activité est celle qu'elle choisit avec vous, en ayant l'occasion d'essayer avant de s'engager.` : `Ce rapport est un guide, pas une prescription. ${cName} reste le meilleur expert de lui-même. La meilleure activité est celle qu'il choisit avec vous, en ayant l'occasion d'essayer avant de s'engager.`;

  return (
    <Document>
      {/* PAGE 1 — COVER + INTRO + DIMENSIONS */}
      <Page size="A4" style={s.page}>
        <View style={s.coverBg}>
          <Image style={s.logo} src={require('./matchkid-logo.png')} />
          <Text style={s.coverLabel}>QUESTIONNAIRE PROFIL ENFANT</Text>
          <Text style={s.coverTitle}>{cName}</Text>
          <Text style={s.coverSub}>{childAge ? childAge + (childAge.includes('ans') ? '' : ' ans') : ''}{gender ? ' - ' + gender : ''}</Text>
          <View style={s.coverBadge1}>
            <Text style={s.coverProfileName}>{pName}</Text>
            <Text style={s.coverProfileLabel}>Profil principal</Text>
          </View>
          <View style={s.coverBadge2}>
            <Text style={[s.coverProfileName, { fontSize: 13 }]}>{sName}</Text>
            <Text style={s.coverProfileLabel}>Profil secondaire</Text>
          </View>
          {data.citation ? <Text style={s.coverCitation}>{clean(data.citation)}</Text> : null}
          <Text style={s.coverMeta}>Rapport etabli sur la base du questionnaire - {today}</Text>
        </View>

        {data.intro ? (
          <View>
            <View style={s.secH}><Text style={s.secHText}>CE QUE LE QUESTIONNAIRE REVELE</Text></View>
            <Text style={s.introText}>{clean(data.intro)}</Text>
          </View>
        ) : null}

        {data.dimensions.length > 0 ? (
          <View>
            <View style={s.secH}><Text style={s.secHText}>LES 4 DIMENSIONS DU PROFIL</Text></View>
            {data.dimensions.map((dim, i) => (
              <View key={i} style={s.dimRow}>
                <Text style={s.dimLabel}>{dim.label}</Text>
                <Text style={s.dimTitle}>{clean(dim.title)}</Text>
                <Text style={s.dimDesc}>{clean(dim.desc)}</Text>
              </View>
            ))}
          </View>
        ) : null}

        <View style={s.footer}>
          <Text style={s.footerText}>Matchkid - Rapport confidentiel</Text>
          <Text style={s.footerText}>{today}</Text>
        </View>
      </Page>

      {/* PAGE 2 — Recommandations + Conseils */}
      <Page size="A4" style={s.page} wrap>
        <View style={s.pageHeader}>
          <Image style={{ width: 80, height: 20 }} src={require('./matchkid-logo.png')} />
          <Text style={s.pageHeaderText}>{headerRight}</Text>
        </View>

        {data.top3.length > 0 ? (
          <View>
            <View style={s.secH}><Text style={s.secHText}>RECOMMANDATIONS D'ACTIVITES - TOP 3</Text></View>
            {data.top3.map((act, i) => (
              <View key={i} style={s.actCard}>
                <Text style={s.actRank}>{act.rank}</Text>
                <Text style={s.actName}>{clean(act.name)}</Text>
                <Text style={s.actWhy}>{clean(act.why)}</Text>
                {act.tags ? <Text style={s.actTags}>{clean(act.tags)}</Text> : null}
              </View>
            ))}
          </View>
        ) : null}

        {data.explore.length > 0 ? (
          <View>
            <View style={s.secH}><Text style={s.secHText}>A EXPLORER AUSSI</Text></View>
            {data.explore.map((item, i) => (
              <View key={i} style={s.exploreRow}>
                <Text style={s.exploreName}>- {clean(item.name)}</Text>
                <Text style={s.exploreWhy}>{clean(item.why)}</Text>
              </View>
            ))}
          </View>
        ) : null}

        {data.avoid.length > 0 ? (
          <View>
            <View style={s.secHRed}><Text style={s.secHText}>CE QUI NE LUI CORRESPOND PAS</Text></View>
            {data.avoid.map((item, i) => (
              <View key={i} style={s.avoidCard}>
                <Text style={s.avoidName}>- {clean(item.name)}</Text>
                <Text style={s.avoidWhy}>{clean(item.why)}</Text>
              </View>
            ))}
          </View>
        ) : null}


        <View style={s.pageHeader}>
          <Image style={{ width: 80, height: 20 }} src={require('./matchkid-logo.png')} />
          <Text style={s.pageHeaderText}>{headerRight}</Text>
        </View>

        {data.conseils.length > 0 ? (
          <View>
            <View style={s.secH}><Text style={s.secHText}>CONSEILS POUR VOUS, PARENTS</Text></View>
            {data.conseils.map((conseil, i) => (
              <View key={i} style={s.conseilCard}>
                <View style={s.conseilRow}>
                  {conseil.num ? <Text style={s.conseilNum}>{conseil.num} -</Text> : null}
                  <Text style={s.conseilTitle}>{clean(conseil.title)}</Text>
                </View>
                {conseil.text ? <Text style={s.conseilText}>{clean(conseil.text)}</Text> : null}
              </View>
            ))}
          </View>
        ) : null}

        {data.synthese.length > 0 ? (
          <View style={{ marginTop: 12 }}>
            <View style={s.secHGold}><Text style={s.secHTextDark}>EN RESUME - LES ACTIVITES LES PLUS ADAPTEES</Text></View>
            <View style={s.tableHeader}>
              <Text style={[s.tableHeaderText, { width: 150 }]}>Activite</Text>
              <Text style={[s.tableHeaderText, { width: 60 }]}>Adequation</Text>
              <Text style={[s.tableHeaderText, { flex: 1 }]}>Pourquoi</Text>
            </View>
            {data.synthese.map((row, i) => (
              <View key={i} style={[s.tableRow, { backgroundColor: i % 2 === 0 ? WHITE : TEAL_PALE }]}>
                <Text style={s.tableCell1}>{clean(row.name)}</Text>
                <Text style={s.tableCell2}>{clean(row.adequation)}</Text>
                <Text style={s.tableCell3}>{clean(row.why)}</Text>
              </View>
            ))}
          </View>
        ) : null}

        <View style={s.noteBox}>
          <Text style={s.noteText}>
            {noteText}
          </Text>
        </View>

        <View style={s.footer}>
          <Text style={s.footerText}>Matchkid - Rapport confidentiel</Text>
          <Text style={s.footerText}>{today}</Text>
        </View>
      </Page>
    </Document>
  );
}

export function DownloadButton({ childName, childAge, gender, primaryProfile, secondaryProfile, reportText }) {
  return (
    <PDFDownloadLink
      document={<MatchkidReport childName={childName} childAge={childAge} gender={gender} primaryProfile={primaryProfile} secondaryProfile={secondaryProfile} reportText={reportText} />}
      fileName={`matchkid-rapport-${(childName || 'enfant').toLowerCase().replace(/\s+/g, '-')}.pdf`}
      style={{ textDecoration: 'none' }}
    >
      {({ loading }) => (
        <button style={{
          background: loading ? '#9BC4C4' : '#1B6B6B',
          color: '#fff', border: 'none', borderRadius: 100,
          padding: '14px 32px', fontSize: 16, fontWeight: 700,
          cursor: loading ? 'not-allowed' : 'pointer', width: '100%',
          marginTop: 8
        }}>
          {loading ? 'Preparation du rapport...' : 'Telecharger mon rapport PDF'}
        </button>
      )}
    </PDFDownloadLink>
  );
}
